import { reactive, ref, toRefs, computed, nextTick, toRef } from 'vue'
import { clone } from '@/common/js/just.js'
import { isArray } from '@/common/js/utils.js'
import { MessageBox } from 'element-ui'
import { Message } from 'view-design'
import { getSession } from '@/common/js/api'
import convertData, { reserveConvertData } from '@/data/toutiao/convert_data.js'

import {
  findPlanTaskById,
  updatePlanTaskByIdNew,
  submitNewToutiaoBatchTask,
  submitToutiaoJubaoBatchTask
} from '@/api/toutiao.local.model'
import { getAccountById } from '@/api/ptom.data.model.js'
import { getCaoHuaGames, adAccount } from '@/api/common.model'

import { getConfigFormModel } from '@/views/create/bytedance/model/form.js'
import { useProjectParamStates, usePageStates } from './useCommonComputeds.js'
import { AMEME_TYPE, CREATE_TYPE_ENUM, APP_PROMOTION_TYPE, MATERIAL_STYLE, PROJECT_AUDIENCE_TYPE, DELIVERY_TYPE } from '../consts/index.js'
import { buildTargetingBasedPlanRows, appendMaterialImageList, normalizeV3SubmitTargetings } from './useToutiaoPlansPreview.js'

const taskIds = getSession('taskIds') || []
let taskDupIndex = 1

export function getModifySubmitTargetings(row = {}) {
  if (row.isV3 && row.project?.audience_type === PROJECT_AUDIENCE_TYPE.UNLIMITED) {
    return [{ targetingParamJson: null }]
  }
  return row.targetings || []
}

export const useToutiaoTask = () => {
  const taskModel = reactive({
    taskList: [],
    originTasks: [],
    currTaskId: taskIds[0],
    loadingTaskId: null,
    pageLoading: false,
    submitLoading: false,
    taskLoadFinished: false
  })
  const basicFormRef = ref(null)
  const keywordSearchFormRef = ref(null)
  const toutiaoTargetingFormRef = ref(null)
  const toutiaoPlanFormRef = ref(null)
  const marketingSceneFormRef = ref(null)
  const bidFormRef = ref(null)

  const { isDuplicate } = usePageStates()

  const currTask = computed({
    get() {
      const task = taskModel.taskList.find((task) => task.taskId === taskModel.currTaskId)
      return task || getConfigFormModel()
    },
    set(task) {
      taskModel.taskList.splice(taskIndex.value, 1, task)
    }
  })

  const taskIndex = computed(() => {
    return taskModel.taskList.findIndex((task) => task.taskId === taskModel.currTaskId)
  })

  const adAccount = computed(() => {
    const [adAccount] = currTask.value.accounts
    return adAccount || {}
  })

  const taskStates = computed(() => taskModel.taskList.map((task) => task.taskStates))

  const projectForm = computed({
    get() {
      return currTask.value.project
    },
    set(form) {
      taskModel.taskList.splice(taskIndex.value, 1, {
        ...taskModel.taskList[taskIndex.value],
        project: form
      })
    }
  })

  const adCreateType = computed(() => {
    return {
      isNew: projectForm.value.createType === CREATE_TYPE_ENUM.NEW,
      isInherit: projectForm.value.createType === CREATE_TYPE_ENUM.INHERIT
    }
  })

  const { isReservation } = useProjectParamStates(projectForm)

  const convertList = computed(() => {
    return isReservation.value ? reserveConvertData : convertData
  })

  const onAccountChange = async (ad_account_id) => {
    const accountInfo = await getAccountById({ ad_account_id })
    taskModel.taskList.splice(taskIndex.value, 1, {
      ...taskModel.taskList[taskIndex.value],
      accounts: [accountInfo],
      ad_account_id: accountInfo.id
    })
    return Promise.resolve()
  }

  const onTaskTabRemove = (taskId, onTaskChange) => {
    const newList = taskModel.taskList.filter(task => task.taskStates.id !== taskId)
    if (taskId === currTask.value.taskId) {
      onTaskChange(newList[0].taskStates.id)
    }
    taskModel.taskList = newList
  }

  const onTaskDuplicate = () => {
    const originTask = taskModel.originTasks.find((t) => t.taskId === currTask.value.taskId)
    const newOriginTask = clone(originTask)
    const newTask = clone(currTask.value)
    const taskId = `复制任务${taskDupIndex++}`
    newTask.project.plan_name += `_${taskId}`
    newTask.taskStates.filled = false
    newTask.taskStates.id = taskId
    newTask.taskId = taskId
    newOriginTask.taskId = taskId
    taskModel.taskList.push(newTask)
    taskModel.originTasks.push(newOriginTask)
  }

  const getTaskList = async () => {
    taskModel.pageLoading = true

    try {
      const originTasks = await findPlanTaskById({ taskIds })
      const taskList = []

      for (let i = 0; i < originTasks.length; i++) {
        const item = originTasks[i]

        // 初始化任务状态结构
        taskList.push({
          taskStates: { id: item.taskId, filled: false }
        })
      }

      taskModel.taskList = taskList
      taskModel.originTasks = originTasks
      return Promise.resolve()
    } catch (e) {
      console.log('getTaskData error output: ', e)
      taskModel.pageLoading = false
      return Promise.reject(new Error('获取任务列表失败'))
    }
  }

  /**
   * @description: 处理广告参数
   * @return {*}
   */
  function handlePlanForm() {
    // 每次切换任务时都将广告信息进行保存更新到currTask中，以免切换后丢失
    const planForm = toutiaoPlanFormRef.value.getForm()
    currTask.value = {
      ...currTask.value,
      plan: planForm
    }
  }

  /**
   * @description: 处理定向参数
   * @return {*}
   */
  function handleTargetingForm() {
    // 每次切换任务时都将定向进行保存更新到currTask中，以免切换后丢失
    const [targeting] = currTask.value.targetings
    if (!targeting || !toutiaoTargetingFormRef.value) return
    targeting.targetingParamJson = toutiaoTargetingFormRef.value.getForm()
    currTask.value = {
      ...currTask.value,
      targetings: [targeting]
    }
  }

  function getConvertActions(projectForm) {
    const convert = convertList.value.find(c => c.key === projectForm.convertId)
    const optimizeGoal = projectForm.delivery_setting?.optimize_goal || {}
    return {
      external_action: convert?.external_action || optimizeGoal.external_action,
      deep_external_action: convert?.deep_external_action || optimizeGoal.deep_external_action
    }
  }

  /**
   * @description: 原项目提交参数
   * @return {*}
   */
  function buildOriginPlanParams(originTask, planParams) {
    planParams.ad_account_id = originTask.ad_account_id
    planParams.campaign_params = originTask.campaign_params
    // outer_plan_params 以原任务为基础，但 operation 与版位(delivery_range)保留用户当前编辑
    // 原任务 operation 可能为 null(失败任务)、delivery_range 为历史版位，直接整体覆盖会提交 operation:null 与错乱版位
    planParams.outer_plan_params = {
      ...originTask.outer_plan_params,
      operation: planParams.outer_plan_params.operation || 'DISABLE',
      delivery_range: planParams.outer_plan_params.delivery_range
    }
    planParams.targetings = { ...originTask.targetings }

    return planParams
  }

  const onTaskSubmit = async (validatorDone) => {
    try {
      await validatorDone()
    } catch (e) {
      console.log('onTaskSubmit error output: ', e)
      e.message && Message.error(e.message)

      return
    }

    const allFilled = taskStates.value.every((s) => s.filled)
    if (!allFilled) {
      return Message.error('请切换任务编号，首次加载数据成功方可提交')
    }

    const isV3Batch = taskModel.taskList.every((row) => row.isV3)
    if (!isV3Batch && taskModel.taskList.some((row) => row.isV3)) {
      return Message.error('智擎版与旧版任务不能混合提交')
    }

    console.log(' taskModel.taskList  output: ', taskModel.taskList)
    console.log(' taskModel.originTasks  output: ', taskModel.originTasks)

    const planlist = taskModel.taskList
      .map((row) => {
        const { operation: projectOperation, plan_name, star_task, star_auto_material_addition_switch } = row.project
        const { operation: creativeOperation, title_material_list, anchor_material_list, web_url_material_list, original_video_title } = row.plan
        const originTask = taskModel.originTasks.find((t) => t.taskId === row.taskId)

        const { external_action, deep_external_action } = getConvertActions(row.project)
        const submitRow = { ...row, targetings: getModifySubmitTargetings(row) }
        let [plan] = buildTargetingBasedPlanRows(submitRow)
        if (row.isV3) plan.targetings = normalizeV3SubmitTargetings(plan.targetings)
        plan = appendMaterialImageList(plan, row.creatives[0])

        plan.taskId = row.taskId
        plan.ad_account_id = row.ad_account_id
        plan.task_progress = row.task_progress
        plan.task_status = row.task_status
        plan.campaign_params = { project_name: plan_name }
        plan.inner_plan_params.plan_name = plan_name

        // 项目、广告是否开启
        plan.outer_plan_params.operation = projectOperation || 'DISABLE'
        plan.creative_params.operation = creativeOperation || 'DISABLE'
        // 没有实际作用的参数，updatePlanTaskByIdNew 修改时接口需要提交
        plan.creative_params.creative_material_mode = 'STATIC_ASSEMBLE'
        if (isArray(web_url_material_list) && web_url_material_list?.length) {
          plan.creative_params.web_url_material_list = web_url_material_list
        }

        // 根据优化目标名称选择
        plan.outer_plan_params.external_action = external_action
        plan.outer_plan_params.deep_external_action = deep_external_action

        // 创意标题
        const normalizedTitleMaterials = title_material_list.map(t => ({ ...t, title: t.title.trim() })).filter(t => !!t.title)
        const isLegacyAutoMaterial = !row.isV3 && star_task?.star_task_id && star_auto_material_addition_switch === 'ON'
        if (isLegacyAutoMaterial) {
          // 达人视频手动添加标题
          if (original_video_title === 'OFF') {
            plan.creative_params.title_material_list = normalizedTitleMaterials
          }
        } else {
          // 广告素材需要传入title_material_list。直播素材不需要
          const { materials_type } = plan.creative_params
          if (materials_type === MATERIAL_STYLE.PROMOTION_MATERIALS || typeof materials_type === 'undefined') {
            plan.creative_params.title_material_list = normalizedTitleMaterials
          }
        }
        if (anchor_material_list?.length) {
          plan.creative_params.anchor_material_list = anchor_material_list
        }

        const { plan: planHelper, isCheck, ...restParams } = plan
        console.log('restParams output: ', restParams)
        const finalPlanParams = row.project.createType === CREATE_TYPE_ENUM.NEW ? restParams : buildOriginPlanParams(originTask, restParams)
        if (row.isV3) {
          finalPlanParams.outer_plan_params.audience_type = row.project.audience_type
          delete finalPlanParams.inner_plan_params.star_task_id_list
          delete finalPlanParams.outer_plan_params.star_task_id_list
          finalPlanParams.outer_plan_params.delivery_type = DELIVERY_TYPE.NORMAL
        }
        return finalPlanParams
      })
    console.log('planlist output: ', planlist)
    taskModel.submitLoading = true
    const submitRequest = isDuplicate.value
      ? (isV3Batch
        ? submitToutiaoJubaoBatchTask({ operation: 'ENABLE', planlist, copy_task_bool: 1 })
        : submitNewToutiaoBatchTask({ planlist, copy_task_bool: 1 }))
      : updatePlanTaskByIdNew({ planlist })

    submitRequest
      .then((data) => {
        if (data) {
          Message.success('提交成功，2秒后自动关闭页面')
          setTimeout(() => {
            window.close()
          }, 2000)
        } else {
          taskModel.submitLoading = false
        }
      })
      .catch(() => {
        taskModel.submitLoading = false
      })
  }

  const onTaskCancel = () => {
    MessageBox.confirm('确认取消修改?', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
      .then(() => {
        window.close()
      })
  }

  return {
    ...toRefs(taskModel),
    adCreateType,

    basicFormRef,
    keywordSearchFormRef,
    toutiaoTargetingFormRef,
    toutiaoPlanFormRef,
    marketingSceneFormRef,
    bidFormRef,

    convertList,

    adAccount,
    currTask,
    taskIndex,
    taskStates,
    projectForm,
    onAccountChange,
    handleTargetingForm,
    handlePlanForm,
    onTaskTabRemove,
    onTaskDuplicate,
    getTaskList,
    onTaskSubmit,
    onTaskCancel
  }
}

export const useToutiaoData = () => {
  const commonDataModel = reactive({
    mediaAccountList: []
  })

  adAccount({ mediaId: 10, authFlag: 1 }).then(data => {
    commonDataModel.mediaAccountList = data
  })

  return {
    ...toRefs(commonDataModel)

  }
}

export const useAnchorRelatedType = (toutiaoPlanFormRef, currTask) => {
  const anchorModel = reactive({
    showAnchorRelatedTypeModal: false
  })

  function onModifyModalCancel() {
    anchorModel.showAnchorRelatedTypeModal = false
  }
  function onAnchorRelatedTypeConfirm(selectedAnchor) {
    const { anchor_name, anchor_type, anchor_id } = selectedAnchor || {}
    if (!anchor_name) return Message.warning('请选择锚点')

    toutiaoPlanFormRef.value.setForm({
      ...currTask.value.plan,
      anchor_name,
      anchor_material_list: [
        {
          anchor_type,
          anchor_id
        }
      ]
    })
    onModifyModalCancel()
  }
  return {
    ...toRefs(anchorModel),
    onModifyModalCancel,
    onAnchorRelatedTypeConfirm
  }
}

export const useTitles = (toutiaoPlanFormRef, currTask) => {
  const titlesModel = reactive({
    titleSelectorModal: false,
    currInputIndex: -1
  })
  const titleListCount = computed(() => {
    const planForm = toutiaoPlanFormRef.value.getForm()
    return planForm.title_material_list.length
  })

  function addTitleItem() {
    if (titleListCount.value >= 10) return
    const planForm = toutiaoPlanFormRef.value.getForm()
    planForm.title_material_list.push({
      title: ''
    })

    toutiaoPlanFormRef.value.setForm(planForm)
  }

  function removeTitleItem(index) {
    const planForm = toutiaoPlanFormRef.value.getForm()
    planForm.title_material_list.splice(index, 1)
    toutiaoPlanFormRef.value.setForm(planForm)
  }

  function showTitleSelectModal(inputIndex) {
    titlesModel.titleSelectorModal = true
    titlesModel.currInputIndex = inputIndex
  }

  function onTitleSelectConfirm(title) {
    const planForm = toutiaoPlanFormRef.value.getForm()
    // 最多添加10个标题
    const count = planForm.title_material_list.length
    const maxTitles = title.slice(0, 10 - count)
    planForm.title_material_list.push(...maxTitles.map(t => ({ title: t.docContent })))
    toutiaoPlanFormRef.value.setForm(planForm)
    onTitleSelectClose()
  }

  function onTitleSelectClose() {
    titlesModel.titleSelectorModal = false
    titlesModel.currInputIndex = -1
  }
  return {
    ...toRefs(titlesModel),

    titleListCount,
    addTitleItem,
    removeTitleItem,
    showTitleSelectModal,
    onTitleSelectClose,
    onTitleSelectConfirm
  }
}

export const useLandingPages = (toutiaoPlanFormRef, currTask) => {
  const landingPageModel = reactive({
    landingPageSelectorModal: false,
    currInputIndex: -1
  })

  const pageListCount = computed(() => {
    const { web_url_material_list } = toutiaoPlanFormRef.value.getForm()
    return web_url_material_list?.length || 0
  })

  function addLandingPageItem() {
    if (pageListCount.value >= 10) return
    const planForm = toutiaoPlanFormRef.value.getForm()
    planForm.web_url_material_list.push('')
    toutiaoPlanFormRef.value.setForm(planForm)
  }

  function removeLandingPageItem(index) {
    const planForm = toutiaoPlanFormRef.value.getForm()
    planForm.web_url_material_list.splice(index, 1)
    toutiaoPlanFormRef.value.setForm(planForm)
  }

  function showLandingPageSelectModal(inputIndex) {
    landingPageModel.landingPageSelectorModal = true
    landingPageModel.currInputIndex = inputIndex
  }

  function onLandingPageSelectConfirm(pageInfo) {
    const planForm = toutiaoPlanFormRef.value.getForm()
    planForm.web_url_material_list.splice(landingPageModel.currInputIndex, 1, pageInfo.url)
    toutiaoPlanFormRef.value.setForm(planForm)
    onLandingPageSelectClose()
  }

  function onLandingPageSelectClose() {
    landingPageModel.landingPageSelectorModal = false
    landingPageModel.currInputIndex = -1
  }
  return {
    // landingPageSelectorModal,
    ...toRefs(landingPageModel),

    pageListCount,
    addLandingPageItem,
    removeLandingPageItem,
    showLandingPageSelectModal,
    onLandingPageSelectClose,
    onLandingPageSelectConfirm
  }
}
